// Hook: gestiona suscripción a push notifications del módulo Guías.
// Aislado de Alarmas/Codelco. Lee VAPID pública desde backend y registra al SW existente (/sw.js).
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { playNotificationSound } from '../lib/notificationSounds';

const SOUND_PREF_KEY = 'guias_push_sound';

export const getSoundPreference = () => {
    try { return localStorage.getItem(SOUND_PREF_KEY) || 'default'; }
    catch { return 'default'; }
};

export const setSoundPreference = (key) => {
    try { localStorage.setItem(SOUND_PREF_KEY, key); } catch {}
};

const urlBase64ToUint8Array = (base64) => {
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const base64Clean = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64Clean);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

export function useGuiasPushSubscription({ enabled = true } = {}) {
    const [status, setStatus] = useState('idle'); // 'idle' | 'unsupported' | 'denied' | 'granted' | 'subscribing' | 'subscribed' | 'error'
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined') return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            setStatus('unsupported');
            return;
        }
        const perm = Notification.permission;
        if (perm === 'denied') setStatus('denied');
        else if (perm === 'granted') {
            subscribe(true).catch(() => {});
        }

        // Escuchar postMessage del SW para reproducir sonido custom si es push de guías.
        // Identificamos push de guías por el prefijo "[App Name]" en el title (configurable).
        const handler = (ev) => {
            if (!ev.data || ev.data.type !== 'PUSH_RECEIVED') return;
            const payload = ev.data.payload || {};
            const title = payload.title || '';
            // Match: "[<app_name>] ..." → es del módulo guías
            const looksLikeGuias = /^\[[^\]]+\]/.test(title);
            if (!looksLikeGuias) return;
            // Sonido: el backend puede mandarlo, o usar preferencia local
            const soundKey = payload.data?.sound || getSoundPreference();
            playNotificationSound(soundKey);
        };
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handler);
        }
        return () => {
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handler);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    const subscribe = async (silent = false) => {
        try {
            setStatus('subscribing');
            if (Notification.permission !== 'granted') {
                const p = await Notification.requestPermission();
                if (p !== 'granted') {
                    setStatus(p === 'denied' ? 'denied' : 'idle');
                    return false;
                }
            }
            const reg = await navigator.serviceWorker.ready;
            const { data } = await api.get('/guias/push/vapid-public-key');
            const publicKey = data.public_key;
            if (!publicKey) {
                setStatus('error');
                setError('VAPID no configurada en el servidor');
                return false;
            }
            const existing = await reg.pushManager.getSubscription();
            const sub = existing || await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
            await api.post('/guias/push/subscribe', { subscription: sub.toJSON() });
            setStatus('subscribed');
            return true;
        } catch (e) {
            console.error('[Guias Push] subscribe error:', e);
            setStatus('error');
            setError(e?.message || 'Error suscribiendo');
            return false;
        }
    };

    const unsubscribe = async () => {
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) await sub.unsubscribe();
            await api.delete('/guias/push/subscribe');
            setStatus('idle');
        } catch (e) {
            console.error('[Guias Push] unsubscribe error:', e);
        }
    };

    return { status, error, subscribe, unsubscribe };
}
